import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { Line } from "rc-progress"
import styled from "@emotion/styled"
import { queries } from "@nutrafol/nutrafol-ui-kit/styles/mediaqueries"
import theme from "@nutrafol/nutrafol-ui-kit/styles/theme"
import { TextElementMen } from "@nutrafol/nutrafol-ui-kit/dist/TextElementMen"
import { Box } from "@rebass/grid/emotion"

import { revealOrHideProgress } from "../../actions"

const STEPS = 7

const Container = styled.div`
  width: calc(100% - 16px);
  margin-left: 8px;
  height: 15px;
  position: absolute;
  top: 72px;
  ${queries.medium`
    width: 100%;
    margin-left: 0;
    top: 96px;
  `}
  ${queries.large`
    top: 104px;
  `}
  .line-holder {
    padding-right: 2px;
    background-color: transparent;
    vertical-align: top;
    font-size: 0;
    &:last-of-type {
      padding-right: 0;
      width: calc(100% / ${STEPS});
      @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
        width: calc(100% / ${STEPS} - 1px);
      }
    }
  }
  .rc-progress-line {
    border-radius: 8px;
    display: inline-block;
    width: 100%;
    height: 7px;
    margin-right: 2px;
    background: ${theme.wild.color.master.base.alt.secondary};
    &:last-of-type {
      width: 100%;
      margin-right: 0;
    }
    ${queries.medium`
      width: 100%;
      margin-right: 5px;
    `}
  }
  #bar-0 {
    margin-left: 0;
  }
  .progress-arrow {
    display: block;
    margin: 0;
  }
`

const LineHolder = styled.div`
  width: calc(100% / ${STEPS});
  display: inline-block;
`

const LegendHolder = styled(Box)`
  width: 100%;
`

const AlertBox = styled.div`
  background-color: #cbe6ef;
  padding: 8px;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  margin: 16px 8px;
  gap: 8px;

  @media (min-width: 1024px) {
    margin: 14px 0 16px 0;
  }

  svg {
    min-width: 20px;
    width: 20px;
    min-height: 20px;
    height: 20px;

    path {
      stroke: #07253a;
    }
  }

  p {
    line-height: 1.3 !important;
    color: #07253a;

    @media (min-width: 1024px) {
      font-size: 16px !important;
    }
  }
`

const ProgressBar = (props) => {
  const { currentSection, revealOrHideProgress, currentQuestionId, location } =
    props

  const [resultsPage, setResultsPage] = useState(false)

  const progressMap = {
    age: 1,
    firstExperience: 2,
    hairHistory: 3,
    hairHistoryPart2: 4,
    hairToday: 5,
    hairGoals: 6,
    healthFocus: 7,
  }

  let progInt = progressMap[currentQuestionId]
  const isSaveSection = currentSection === "save-results"

  // NT2-14260 Men's quiz | After passing quiz step, the previous quiz step is briefly shown.

  //todo figure real cause
  if ("hair-history" === currentSection && progInt < 3) {
    progInt = 3
  }

  const isActiveLine = (idx) => {
    return isSaveSection || progInt > idx
  }
  const getStepColor = (idx) => {
    if (isActiveLine(idx)) {
      return "#0F3956"
    }
    return "rgba(0,0,0,0)"
  }

  const renderProgressBars = () => {
    return [...Array(STEPS)].map((q, idx) => {
      let id = `bar-${idx}`
      return (
        <React.Fragment key={`bar-${idx}`}>
          <LineHolder
            className={`line-holder line-holder-active-${isActiveLine(idx)}`}
            key={id}
          >
            <Line
              key={`${idx}-bar`}
              percent={100}
              id={id}
              strokeColor={getStepColor(idx)}
              strokeLinecap={"round"}
            />
            <LegendHolder mt={["4px"]} className={`legend-copy`}>
              {progInt === idx + 1 && !isSaveSection ? (
                <>
                  <TextElementMen
                    text={`${progInt}/${STEPS}`}
                    element="p"
                    className="mono--small hide-md-up text-master-secondary-default text-center"
                  />
                  <TextElementMen
                    text={`QUESTION ${progInt}/${STEPS}`}
                    element="p"
                    className="mono--small show-md-up text-master-secondary-default text-center"
                  />
                </>
              ) : null}
            </LegendHolder>
          </LineHolder>
        </React.Fragment>
      )
    })
  }

  useEffect(() => {
    revealOrHideProgress(false)
  }, [])

  useEffect(() => {
    if (typeof window !== `undefined`) {
      setResultsPage(
        currentSection === "results" ||
          window.location.href.includes("/results")
      )
    }
  }, [currentSection])

  const showAlertBox =
    !resultsPage &&
    !!currentSection &&
    !!currentQuestionId &&
    currentSection !== "save-results" &&
    location.pathname !== "/hair/men/quiz/"

  const showRibbon = false

  return (
    <>
      {showAlertBox && showRibbon ? (
        <AlertBox className="flex men">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M16.6667 10.4784V16.3478C16.6667 16.5208 16.5964 16.6867 16.4714 16.809C16.3464 16.9313 16.1768 17 16 17H4C3.82319 17 3.65362 16.9313 3.52859 16.809C3.40357 16.6867 3.33333 16.5208 3.33333 16.3478V10.4784M10 6.5654V17M10 6.5654C10 6.5654 12.6567 6.56536 14.0658 5.345C14.89 4.63088 14.8442 3.34938 14.0658 2.58798C13.2875 1.82658 11.9775 1.78175 11.25 2.58798C10 3.96649 10 6.5654 10 6.5654ZM10 6.5654C10 6.5654 7.34332 6.56536 5.93415 5.345C5.10998 4.63088 5.15582 3.34938 5.93415 2.58798C6.71248 1.82658 8.02248 1.78175 8.74998 2.58798C9.99998 3.96649 10 6.5654 10 6.5654ZM2.66667 6.5654H17.3333C17.7015 6.5654 18 6.85739 18 7.21757V9.82621C18 10.1864 17.7015 10.4784 17.3333 10.4784H2.66667C2.29848 10.4784 2 10.1864 2 9.82621V7.21757C2 6.85739 2.29848 6.5654 2.66667 6.5654Z"
              stroke="#07253A"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <TextElementMen
            text="Unlock <span class='font-dinamoBold'>free Collagen Builder</span> when you subscribe to your personalized routine."
            element="p"
            className="body--small variant-b-"
          />
        </AlertBox>
      ) : null}

      <Container className="row progress-bar men">
        {!!currentSection &&
        !!currentQuestionId &&
        !resultsPage &&
        currentSection !== "save-results"
          ? renderProgressBars()
          : null}
      </Container>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    quizProgress: state.progressReducer.quizProgress,
    currentSection: state.sectionReducer.currentSection,
    sectionLength: state.sectionLengthReducer.sectionLength,
    hideProgressForName: state.revealProgressReducer.hideProgressForName,
    currentQuestionId: state.questionIdReducer.currentQuestionId,
    location: state.locationReducer,
  }
}

export default connect(mapStateToProps, { revealOrHideProgress })(ProgressBar)
